// Modos de timeline predefinidos para diferentes tipos de negócio
export type TimelineMode = "atelie" | "confeccao" | "grafica" | "custom";

export interface TimelineStepTemplate {
  key: string;
  label: string;
  description: string;
  order: number;
}

// Timeline modo "Ateliê" - para consertos e ajustes
export const TIMELINE_ATELIE: TimelineStepTemplate[] = [
  {
    key: "RECEPÇÃO",
    label: "Recepção",
    description: "Pedido recebido e analisado",
    order: 1,
  },
  {
    key: "DIAGNÓSTICO",
    label: "Diagnóstico",
    description: "Avaliação do problema",
    order: 2,
  },
  {
    key: "ORÇAMENTO",
    label: "Orçamento",
    description: "Aguardando aprovação do cliente",
    order: 3,
  },
  {
    key: "EXECUÇÃO",
    label: "Execução",
    description: "Trabalho em andamento",
    order: 4,
  },
  {
    key: "PROVA",
    label: "Prova",
    description: "Cliente realizando prova",
    order: 5,
  },
  {
    key: "AJUSTES",
    label: "Ajustes Finais",
    description: "Realizando ajustes solicitados",
    order: 6,
  },
  {
    key: "PRONTO",
    label: "Pronto",
    description: "Peça finalizada",
    order: 7,
  },
  {
    key: "ENTREGUE",
    label: "Entregue",
    description: "Pedido entregue ao cliente",
    order: 8,
  },
];

// Timeline modo "Confecção" - para roupas novas/personalizadas
export const TIMELINE_CONFECCAO: TimelineStepTemplate[] = [
  {
    key: "TECIDO",
    label: "Tecido",
    description: "Pedido recebido, aguardando compra de tecido",
    order: 1,
  },
  {
    key: "CORTE",
    label: "Corte",
    description: "Trabalho iniciado, corte em andamento",
    order: 2,
  },
  {
    key: "ESTAMPA",
    label: "Estampa",
    description: "Etapa de estampa/sublimação em andamento",
    order: 3,
  },
  {
    key: "COSTURA",
    label: "Costura",
    description: "Produto aguardando costura/acabamento",
    order: 4,
  },
  {
    key: "OFICINA",
    label: "Oficina",
    description: "Produto enviado para costureira/fornecedor externo",
    order: 5,
  },
  {
    key: "PRONTO",
    label: "Pronto",
    description: "Produto finalizado, pronto para entrega",
    order: 6,
  },
  {
    key: "AGUARDANDO_RETIRADA",
    label: "Aguardando Retirada",
    description: "Produto pronto, aguardando retirada",
    order: 7,
  },
  {
    key: "ENTREGUE",
    label: "Entregue",
    description: "Pedido entregue ao cliente",
    order: 8,
  },
];

// Timeline modo "Gráfica/Estamparia" - para bordados e estampas
export const TIMELINE_GRAFICA: TimelineStepTemplate[] = [
  {
    key: "ARQUIVO",
    label: "Arquivo",
    description: "Recebimento e preparação do arquivo",
    order: 1,
  },
  {
    key: "APROVACAO",
    label: "Aprovação",
    description: "Aguardando aprovação da arte",
    order: 2,
  },
  {
    key: "MONTAGEM",
    label: "Montagem",
    description: "Preparação da máquina e montagem",
    order: 3,
  },
  {
    key: "PRODUCAO",
    label: "Produção",
    description: "Bordado/Estampa em andamento",
    order: 4,
  },
  {
    key: "ACABAMENTO",
    label: "Acabamento",
    description: "Acabamento e limpeza",
    order: 5,
  },
  {
    key: "QUALIDADE",
    label: "Controle Qualidade",
    description: "Verificação de qualidade",
    order: 6,
  },
  {
    key: "PRONTO",
    label: "Pronto",
    description: "Produto pronto para entrega",
    order: 7,
  },
  {
    key: "ENTREGUE",
    label: "Entregue",
    description: "Pedido entregue ao cliente",
    order: 8,
  },
];

// Mapear tipos de pedido aos modos de timeline
export const TIPO_PEDIDO_TO_TIMELINE_MODE: Record<string, TimelineMode> = {
  conserto: "atelie",
  ajuste: "atelie",
  bordado: "grafica",
  camiseta: "confeccao",
  uniforme: "confeccao",
  personalizado: "confeccao",
  catalogo: "confeccao",
  outro: "confeccao",
};

// Função para obter a timeline baseada no tipo de pedido
export function getTimelineForOrderType(orderType: string): TimelineStepTemplate[] {
  const mode = TIPO_PEDIDO_TO_TIMELINE_MODE[orderType] || "confeccao";
  return getTimelineForMode(mode);
}

// Função para obter a timeline baseada no modo
export function getTimelineForMode(mode: TimelineMode): TimelineStepTemplate[] {
  switch (mode) {
    case "atelie":
      return TIMELINE_ATELIE;
    case "grafica":
      return TIMELINE_GRAFICA;
    case "confeccao":
      return TIMELINE_CONFECCAO;
    case "custom":
      return TIMELINE_CONFECCAO; // fallback
    default:
      return TIMELINE_CONFECCAO;
  }
}

// Função para obter o modo a partir do nome da timeline
export function getTimelineModeFromName(name: string): TimelineMode {
  const nameLower = name.toLowerCase();
  if (nameLower.includes("atelie") || nameLower.includes("conserto")) {
    return "atelie";
  } else if (nameLower.includes("grafica") || nameLower.includes("bordado")) {
    return "grafica";
  } else if (nameLower.includes("confeccao") || nameLower.includes("confecção")) {
    return "confeccao";
  }
  return "confeccao";
}

// Modos disponíveis com labels
export const TIMELINE_MODES = [
  { value: "atelie" as TimelineMode, label: "🔧 Ateliê", description: "Para consertos e ajustes" },
  { value: "confeccao" as TimelineMode, label: "👕 Confecção", description: "Para roupas novas" },
  { value: "grafica" as TimelineMode, label: "✨ Gráfica", description: "Para bordados/estampas" },
  { value: "custom" as TimelineMode, label: "⚙️ Personalizado", description: "Criar sua própria timeline" },
];
